import { useState, useEffect } from 'react';
import {
  listRenovationObjectives,
  getObjective,
  rankObjectiveForLogement,
} from '@/api/admin';

interface RenovationObjective {
  key: string;
  label: string;
  description: string;
  icon: string;
  proposed_bouquet_label: string;
  proposed_bouquet_count: number;
}

interface RankedImprovement {
  key: string;
  label: string;
  category: string;
  cost_min_eur: number;
  cost_max_eur: number;
  aids_eur: number;
  kwh_saved: number;
  primary_reduction_pct: number;
  ges_reduction_kgco2_per_m2: number;
  summer_score_delta: number;
  roi_years: number | null;
  objective_score: number;
  relevance: string;
  relevance_message: string | null;
}

interface ProposedBouquet {
  improvements: string[];
  label: string;
  description: string;
  notes: string[];
}

interface ObjectiveRanking {
  objective_key: string;
  objective_label: string;
  objective_description: string;
  ranked_improvements: RankedImprovement[];
  proposed_bouquet: ProposedBouquet;
}

interface ObjectiveListResponse {
  items: RenovationObjective[];
}

interface ObjectiveRankingResponse {
  objective_key: string;
  objective_label: string;
  objective_description: string;
  ranked_improvements: RankedImprovement[];
  proposed_bouquet: ProposedBouquet;
}

const RELEVANCE_COLORS: Record<string, string> = {
  excellent: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  bon: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  ok: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  non_rentable: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  inadapte: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

const CATEGORY_COLORS: Record<string, string> = {
  Enveloppe: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  Systèmes: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  'EnR Production': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  'Confort / Optimisation': 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200',
  Autre: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
};

const ICON_SVG: Record<string, JSX.Element> = {
  target: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  ),
  star: (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
    </svg>
  ),
  'lightning-bolt': (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  ),
  leaf: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  ),
  sun: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  ),
  'solar-panel': (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <rect x="2" y="2" width="20" height="20" rx="2" ry="2" />
      <path d="M8 6h8M8 10h8M8 14h8M8 18h8" />
    </svg>
  ),
  'trending-up': (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l4-4m0 0l4 4m-4-4h18" />
    </svg>
  ),
  'piggy-bank': (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v12M12 6a2 2 0 100 4 2 2 0 000-4z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.5 20.5c0-2.485 2.015-4.5 4.5-4.5s4.5 2.015 4.5 4.5-2.015 4.5-4.5 4.5" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.5 20.5c0-2.485 2.015-4.5 4.5-4.5s4.5 2.015 4.5 4.5-2.015 4.5-4.5 4.5" />
    </svg>
  ),
};

export default function AdminRenovationObjectives() {
  const [objectives, setObjectives] = useState<RenovationObjective[]>([]);
  const [selectedObjective, setSelectedObjective] = useState<string | null>(null);
  const [ranking, setRanking] = useState<ObjectiveRanking | null>(null);
  const [loading, setLoading] = useState(false);
  const [logementId, setLogementId] = useState('');
  const [dpeId, setDpeId] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadObjectives();
  }, []);

  async function loadObjectives() {
    try {
      const res = await
listRenovationObjectives() as ObjectiveListResponse;
      setObjectives(res.items);
      if (res.items.length > 0 && !selectedObjective) {
        setSelectedObjective(res.items[0].key);
      }
    } catch (e) {
      setError('Erreur chargement objectifs');
    }
  }

  async function loadRanking(objectiveKey: string) {
    setLoading(true);
    setError(null);
    try {
      let res: ObjectiveRankingResponse;
      if (logementId) {
        res = await
rankObjectiveForLogement(objectiveKey, {
          logement_id: logementId,
          dpe_id: dpeId || undefined,
        }) as ObjectiveRankingResponse;
      } else {
        res = await
getObjective(objectiveKey) as ObjectiveRankingResponse;
      }
      setRanking(res);
    } catch (e: any) {
      setError(e.message || 'Erreur chargement classement');
    } finally {
      setLoading(false);
    }
  }

  function handleObjectiveChange(key: string) {
    setSelectedObjective(key);
    loadRanking(key);
  }

  if (objectives.length === 0 && !loading) {
    return <div className="text-center py-12 text-gray-500">Aucun objectif configuré</div>;
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Objectifs de rénovation
        </h1>
        <p className="mt-1 text-gray-600 dark:text-gray-400">
          Choisissez un objectif pour classer les améliorations par impact et obtenir
          un bouquet de simulation prêt à l'emploi.
        </p>
      </div>

      {/* Selector */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-lg font-semibold mb-4">Sélectionner un objectif</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {objectives.map((obj) => (
            <button
              key={obj.key}
              onClick={() => handleObjectiveChange(obj.key)}
              className={`relative p-4 rounded-lg border-2 transition-all text-left ${
                selectedObjective === obj.key
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-lg ${selectedObjective === obj.key ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-gray-100 dark:bg-gray-700'}`}>
                  {ICON_SVG[obj.icon] || ICON_SVG.target}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-gray-900 dark:text-white truncate">{obj.label}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">{obj.description}</p>
                  <div className="mt-2 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                    <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">
                      {obj.proposed_bouquet_count} gestes
                    </span>
                  </div>
                </div>
              </div>
              {selectedObjective === obj.key && (
                <div className="absolute -top-2 -right-2 w-5 h-5 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                  ✓
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Contexte logement (optionnel) */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-lg font-semibold mb-4">Contexte logement (optionnel)</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Fournissez un ID logement pour un classement personnalisé selon son DPE réel.
          Sans contexte, le classement est générique.
        </p>
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              ID Logement *
            </label>
            <input
              type="text"
              value={logementId}
              onChange={(e) => setLogementId(e.target.value)}
              placeholder="uuid-du-logement"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              ID DPE (optionnel)
            </label>
            <input
              type="text"
              value={dpeId}
              onChange={(e) => setDpeId(e.target.value)}
              placeholder="uuid-du-dpe"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
          <button
            onClick={() => selectedObjective && loadRanking(selectedObjective)}
            disabled={!logementId || loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Chargement...' : 'Classer pour ce logement'}
          </button>
        </div>
        {error && (
          <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md text-red-700 dark:text-red-300 text-sm">
            {error}
          </div>
        )}
      </div>

      {/* Results */}
      {ranking && (
        <div className="space-y-8">
          {/* Proposed Bouquet */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {ranking.proposed_bouquet.label}
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mt-1">{ranking.proposed_bouquet.description}</p>
              </div>
              <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 text-sm font-medium rounded-full">
                Simulation 1 clic
              </span>
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
              {ranking.proposed_bouquet.improvements.map((imp, idx) => (
                <span key={imp} className="px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-sm text-gray-700 dark:text-gray-300">
                  {idx + 1}. {imp}
                </span>
              ))}
            </div>

            {ranking.proposed_bouquet.notes.length > 0 && (
              <div className="space-y-1">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Notes :</h4>
                <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 space-y-1">
                  {ranking.proposed_bouquet.notes.map((note, i) => (
                    <li key={i}>{note}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Ranked Improvements Table */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold">Classement des améliorations</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {ranking.ranked_improvements.length} améliorations évaluées pour « {ranking.objective_label} »
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-800/50">
                  <tr className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    <th className="px-4 py-3">#</th>
                    <th className="px-4 py-3">Amélioration</th>
                    <th className="px-4 py-3 hidden md:table-cell">Catégorie</th>
                    <th className="px-4 py-3 hidden lg:table-cell">Coût</th>
                    <th className="px-4 py-3 hidden lg:table-cell">Aides</th>
                    <th className="px-4 py-3 hidden lg:table-cell">kWh/an</th>
                    <th className="px-4 py-3 hidden lg:table-cell">Δ Primaire</th>
                    <th className="px-4 py-3 hidden xl:table-cell">Δ GES</th>
                    <th className="px-4 py-3 hidden xl:table-cell">Confort été</th>
                    <th className="px-4 py-3">ROI</th>
                    <th className="px-4 py-3">Score</th>
                    <th className="px-4 py-3">Pertinence</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {ranking.ranked_improvements.map((imp, idx) => (
                    <tr key={imp.key} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                        {idx + 1}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white max-w-xs truncate" title={imp.label}>
                        {imp.label}
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className={`px-2 py-0.5 text-xs rounded-full ${CATEGORY_COLORS[imp.category] || CATEGORY_COLORS.Autre}`}>
                          {imp.category}
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell text-sm text-gray-600 dark:text-gray-400">
                        {imp.cost_min_eur.toLocaleString()}–{imp.cost_max_eur.toLocaleString()} €
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell text-sm text-green-600 dark:text-green-400">
                        {imp.aids_eur > 0 ? imp.aids_eur.toLocaleString() + ' €' : '—'}
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell text-sm text-gray-600 dark:text-gray-400">
                        {imp.kwh_saved > 0 ? imp.kwh_saved.toLocaleString() : '—'}
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell text-sm text-gray-600 dark:text-gray-400">
                        {imp.primary_reduction_pct > 0 ? imp.primary_reduction_pct.toFixed(1) + '%' : '—'}
                      </td>
                      <td className="px-4 py-3 hidden xl:table-cell text-sm text-gray-600 dark:text-gray-400">
                        {imp.ges_reduction_kgco2_per_m2 > 0 ? imp.ges_reduction_kgco2_per_m2.toFixed(1) : '—'}
                      </td>
                      <td className="px-4 py-3 hidden xl:table-cell text-sm text-gray-600 dark:text-gray-400">
                        {imp.summer_score_delta !== 0 ? (imp.summer_score_delta > 0 ? '+' : '') + imp.summer_score_delta : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {imp.roi_years !== null ? imp.roi_years.toFixed(1) + ' ans' : 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                        {imp.objective_score.toFixed(0)} / 100
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-xs rounded-full ${RELEVANCE_COLORS[imp.relevance] || RELEVANCE_COLORS.ok}`}>
                          {imp.relevance}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {selectedObjective && !ranking && !loading && !logementId && (
        <div className="text-center py-12 text-gray-500">
          <p>Sélectionnez un logement pour voir le classement personnalisé,</p>
          <p className="mt-1">ou consultez le bouquet proposé ci-dessus (générique).</p>
        </div>
      )}
    </div>
  );
}